import { CopyIcon } from "@phosphor-icons/react/dist/csr/Copy";
import {
  AttributeItem,
  AttributeList,
} from "@volca/ui/components/ui/attribute-list";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { dayjs } from "@/core/utils/date";
import { formatByteSize } from "@/profitability/document-chip";
import { DOCUMENT_STATUS } from "@/profitability/document-constants";
import { matchedColumnLabel } from "@/profitability/matched-columns";
import { MetaList } from "@/profitability/meta-list";
import type {
  MatchedColumn,
  ProfitabilityDocument,
} from "@/profitability/types";
import {
  type DocumentEntry,
  entryPurpose,
  rowsLabel,
} from "./document-entries";
import { useDocumentDownload } from "./use-document-download";

const fileType = (document: ProfitabilityDocument) => {
  const extension = document.file_name.split(".").pop();
  return extension && extension !== document.file_name
    ? extension.toUpperCase()
    : (document.content_type ?? null);
};

const readLabel = (document: ProfitabilityDocument) =>
  document.status === DOCUMENT_STATUS.ready
    ? document.kind === "text"
      ? "Read as text"
      : "Read"
    : document.status === DOCUMENT_STATUS.pending
      ? "Reading…"
      : "Couldn't read";

/**
 * Everything about the file in one list: its name, type and size, who
 * uploaded it and when, how its read went, the tables read from it, any
 * sheet or column read under the name the template expects, and its id.
 */
export const DocumentDetails = ({
  businessId,
  entry,
  document,
  matches,
}: {
  businessId: string;
  entry: DocumentEntry;
  document: ProfitabilityDocument;
  matches: MatchedColumn[];
}) => {
  const { download, downloading } = useDocumentDownload(
    businessId,
    document.id,
  );
  const uploaded = dayjs(document.created_at);
  return (
    <AttributeList className="max-w-3xl grid-cols-[minmax(9rem,auto)_minmax(0,1fr)]">
      <AttributeItem label="File name">
        <span className="[overflow-wrap:anywhere] whitespace-normal">
          {document.file_name}
        </span>
      </AttributeItem>
      <AttributeItem label="Used as">{entryPurpose(entry)}</AttributeItem>
      <AttributeItem label="Type">{fileType(document)}</AttributeItem>
      <AttributeItem label="Size">
        {formatByteSize(document.byte_size) || null}
      </AttributeItem>
      <AttributeItem label="Uploaded by">
        {document.uploaded_by?.full_name}
      </AttributeItem>
      <AttributeItem label="Uploaded">
        {uploaded.format("MMM D, YYYY [at] h:mm a")}
      </AttributeItem>
      <AttributeItem label="Status" className="items-start">
        <Stack gap={0.5} className="whitespace-normal">
          <span>{readLabel(document)}</span>
          {document.error && (
            <Text variant="caption" tone="destructive">
              {document.error}
            </Text>
          )}
        </Stack>
      </AttributeItem>
      {document.extraction?.processor && (
        <AttributeItem label="Extraction" className="items-start">
          <Stack gap={0.5} className="whitespace-normal">
            <span>
              {document.extraction.fidelity} · {document.extraction.processor}
            </span>
            {document.extraction.warnings?.map((warning) => (
              <Text key={warning} variant="caption" tone="muted">
                {warning}
              </Text>
            ))}
          </Stack>
        </AttributeItem>
      )}
      {document.sheets.length > 0 && (
        <AttributeItem label="Tables" className="items-start">
          <Stack render={<ul />} gap={0.5} className="whitespace-normal">
            {document.sheets.map((sheet) => (
              <li key={sheet.name}>
                <MetaList>
                  <span className="font-medium">{sheet.name}</span>
                  {rowsLabel(sheet.row_count)}
                  {`${sheet.columns.length} ${sheet.columns.length === 1 ? "column" : "columns"}`}
                </MetaList>
              </li>
            ))}
          </Stack>
        </AttributeItem>
      )}
      {matches.length > 0 && (
        <AttributeItem label="Read under other names" className="items-start">
          <Stack render={<ul />} gap={0.5} className="whitespace-normal">
            {matches.map((match) => (
              <li key={`${match.sheet}:${match.column ?? ""}`}>
                {matchedColumnLabel(match)}
              </li>
            ))}
          </Stack>
        </AttributeItem>
      )}
      <AttributeItem label="Document ID">
        <Stack direction="row" gap={1} align="center" className="min-w-0">
          <span className="truncate font-mono text-xs">{document.id}</span>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Copy document ID"
            onClick={() =>
              void navigator.clipboard.writeText(document.id).then(
                () => toast.success("Document ID copied"),
                () => toast.error("The ID could not be copied"),
              )
            }
          >
            <CopyIcon aria-hidden />
          </Button>
        </Stack>
      </AttributeItem>
      <AttributeItem label="Original">
        <Button
          variant="link"
          className="h-auto p-0"
          loading={downloading}
          onClick={download}
        >
          Download {document.file_name}
        </Button>
      </AttributeItem>
    </AttributeList>
  );
};
